import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import App from "./components/App";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import MainFeatures from "./components/MainFeatures";
import ExamplesGallery from "./components/ExamplesGallery";
import HowItWorks from "./components/HowItWorks";
import SocialProof from "./components/SocialProof";
import FAQ from "./components/FAQ";
import BlogPreview from "./components/BlogPreview";
import Footer from "./components/Footer";
import About from "./components/About";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import Contact from "./components/Contact";
import "./index.css";
import BlogList from "./components/BlogList";
import BlogPost from "./components/BlogPost";

const LandingPage = () => {
  // SEO优化
  React.useEffect(() => {
    // 基础Meta标签
    document.title = "RemoveBackground.online - Free AI Background Remover | Best Online Tool 2024";
    
    const metaTags = [
      {
        name: 'description',
        content: 'Professional AI-powered background removal tool. Remove backgrounds from images instantly with perfect precision. Free, secure, and private - 100% local processing with no upload needed. Ideal for e-commerce, portraits, and product photos.'
      },
      {
        name: 'keywords',
        content: 'background remover, remove background online, background removal tool, AI background remover, remove image background, transparent background maker, photo background remover, free background removal, AI image processing, professional photo editing'
      },
      {
        property: 'og:title',
        content: 'RemoveBackground.online - Professional AI Background Removal Tool'
      },
      {
        property: 'og:description',
        content: 'Transform your images instantly with our AI-powered background removal tool. Perfect for professionals and beginners. 100% free, private, and secure - your images never leave your device.'
      },
      {
        name: 'robots',
        content: 'index, follow'
      },
      {
        name: 'googlebot',
        content: 'index, follow'
      },
      {
        property: 'og:site_name',
        content: 'RemoveBackground.online'
      },
      {
        property: 'twitter:creator',
        content: '@removebackground'
      }
    ];

    metaTags.forEach(tag => {
      const metaElement = document.createElement('meta');
      Object.entries(tag).forEach(([key, value]) => {
        metaElement.setAttribute(key, value);
      });
      document.head.appendChild(metaElement);
    });

    // 添加结构化数据
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'RemoveBackground.online',
      'url': 'https://removebackground.online',
      'applicationCategory': 'Image Editor',
      'description': 'Professional AI-powered background removal tool. Free, instant, and secure with 100% local processing.',
      'operatingSystem': 'Any',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD'
      },
      'featureList': [
        'AI-powered background removal',
        'Instant processing',
        '100% privacy - local processing',
        'No registration required',
        'Free to use'
      ]
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(structuredData);
    document.head.appendChild(scriptTag);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* 导航栏组件 */}
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative z-10">
        <HeroSection />
      </div>
      
      {/* Remove Background Section */}
      <div className="relative z-20 py-24" id="bulk-removal">
        <App />
      </div>
    
      {/* 主要功能区 */}
      <div className="relative z-10 py-24 bg-blue-50/50">
        <MainFeatures />
      </div>
      
      {/* 示例展示 */}
      <div className="relative py-24">
        <ExamplesGallery />
      </div>
      
      {/* 工作流程 */}
      <div className="relative py-24 bg-blue-50/50">
        <HowItWorks />
      </div>
      
      {/* 社交证明 */}
      <div className="relative py-24">
        <SocialProof />
      </div>
      
      {/* FAQ */}
      <div className="relative py-24 bg-blue-50/50">
        <FAQ />
      </div>
      
      {/* 博客预览 */}
      <div className="relative py-24">
        <BlogPreview />
      </div>
      
      {/* 底部导航 */}
      <Footer />
    </div>
  );
};

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

createRoot(rootElement).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:id" element={<BlogPost />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
);